import { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Reset from '../../components/User/Reset';
import { useAuthStore } from '../../store/authStore';
import { EyeIcon, EyeSlashIcon } from '@heroicons/react/24/solid';
import { ExclamationCircleIcon } from '@heroicons/react/24/outline';
import SharedButton from '@/global/Button/Button';
import {
  takePostLoginRedirectTarget,
  type PostLoginRedirect,
} from '../../utils/pendingSearchRedirectStorage';
import { UNCONFIRMED_USER_MESSAGE } from '../../utils/resendConfirmationEmail';
import ResendConfirmationEmailButton from '../../components/SignUp/ResendConfirmationEmailButton';

function validateEmail(email) {
  email = email.trim().replace(/\s+/g, '');
  if (email.toLowerCase() === 'mark@ramsey.international') {
    return true;
  }
  return /^[a-zA-Z0-9_\-\.]+@([a-zA-Z0-9\-]+\.)+[a-zA-Z]{1,5}$/.test(email);
}

function encodeRenewSurveyToken(value: string) {
  const bytes = new TextEncoder().encode(value);
  let binary = '';
  bytes.forEach(byte => {
    binary += String.fromCharCode(byte);
  });
  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

type LoginProps = {
  onLoginSuccess?: () => void;
  onClose?: () => void;
  hideCloseButton?: boolean;
};

function Login({ onLoginSuccess }: LoginProps = {}) {
  const navigate = useNavigate();

  const { user, login, logout, isAuthenticating, authError } = useAuthStore();
  const isAuthenticated = !!user;

  const [errorMessage, setErrorMessage] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isPassVisible, setIsPassVisible] = useState(false);
  const [reset, setReset] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isEmailValid, setIsEmailValid] = useState(false);
  const [renewSurveyUrl, setRenewSurveyUrl] = useState('/feedback-survey?type=renewal');

  const loginJustSucceededRef = useRef(false);
  const postLoginTargetRef = useRef<PostLoginRedirect | null>(null);
  const postLoginFallbackHrefRef = useRef<string | null>(null);

  useEffect(() => {
    if (!isAuthenticated || isLoading || isAuthenticating) {
      return;
    }
    if (onLoginSuccess) {
      onLoginSuccess();
      return;
    }
    if (!loginJustSucceededRef.current) {
      return;
    }
    loginJustSucceededRef.current = false;
    const target = postLoginTargetRef.current ?? '/';
    postLoginTargetRef.current = null;
    navigate(target, { replace: true });
  }, [isAuthenticated, navigate, onLoginSuccess, isLoading, isAuthenticating]);

  useEffect(() => {
    if (authError) {
      setErrorMessage(authError);
    }
  }, [authError]);

  useEffect(() => {
    if (errorMessage !== 'User is disabled.' || !isEmailValid) {
      setRenewSurveyUrl('/feedback-survey?type=renewal');
      return;
    }

    const normalizedEmail = email.trim().toLowerCase();
    const rt = encodeRenewSurveyToken(normalizedEmail);
    setRenewSurveyUrl(`/feedback-survey?type=renewal&rt=${encodeURIComponent(rt)}`);
  }, [email, errorMessage, isEmailValid]);

  if (isAuthenticated) {
    return (
      <div className="container txt-contenido">
        <div className="login-container mb-5">
          <div className="signin-container">
            <div className="col-12 text-center">
              <h3 className="login-title" style={{ color: 'white' }}>
                Already Logged In
              </h3>
              <p style={{ color: 'white', marginBottom: '20px' }}>
                You are already authenticated. You can logout to test the login form.
              </p>
              <div className="container-contact100-form-btn">
                <div className="wrap-contact100-form-btn">
                  <div className="contact100-form-bgbtn" />
                  <button
                    className="contact100-form-btn"
                    onClick={async () => {
                      await logout();
                      window.location.reload();
                    }}
                  >
                    Logout
                  </button>
                </div>
              </div>
              <div style={{ marginTop: '20px' }}>
                <Link to="/" style={{ color: 'white' }}>
                  Go to Home
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const togglePassVisibility = () => {
    setIsPassVisible(prevState => !prevState);
  };

  const handleChangeEmail = event => {
    const emailValue = event.target.value.trim().replace(/\s+/g, '');
    setEmail(emailValue);
    setIsEmailValid(validateEmail(emailValue));
  };

  const handleChangePassword = event => {
    const passwordValue = event.target.value;
    setPassword(passwordValue);
  };

  const handleSubmit = async evt => {
    evt.preventDefault();
    setErrorMessage('');

    if (!email || !password) {
      setErrorMessage('Please enter both email and password');
      return;
    }

    if (!isEmailValid) {
      setErrorMessage('Please enter a valid email');
      return;
    }

    try {
      setIsLoading(true);
      await login({ username: email, password });

      if (onLoginSuccess) {
        setTimeout(() => onLoginSuccess(), 100);
      } else {
        const target = takePostLoginRedirectTarget();
        postLoginTargetRef.current = target;
        postLoginFallbackHrefRef.current =
          typeof target === 'string' ? target : `${target.pathname}${target.search}`;
        loginJustSucceededRef.current = true;

        setTimeout(() => {
          if (window.location.pathname === '/login') {
            const href = postLoginFallbackHrefRef.current ?? '/';
            postLoginFallbackHrefRef.current = null;
            window.location.href = href;
          }
        }, 1000);
      }
    } catch (err) {
      console.error('🔐 Login failed:', err);
      const typedError = err as { message: string };
      setErrorMessage(typedError.message || 'Login failed. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleReset = evt => {
    evt.preventDefault();
    setReset(true);
  };

  if (reset) {
    return (
      <div className="apply-trial-page">
        <div className="apply-trial-hero-bg" aria-hidden="true">
          <img src="/static/images/KeyVisuals/hero.png" alt="" className="hero-static-image" />
          <div className="hero-video-overlay" aria-hidden="true" />
        </div>
        <div className="apply-trial-content">
          <Reset setReset={setReset} />
        </div>
      </div>
    );
  }

  return (
    <div className="apply-trial-page">
      <link rel="canonical" href="/login" />
      <div className="apply-trial-hero-bg" aria-hidden="true">
        <img src="/static/images/KeyVisuals/hero.png" alt="" className="hero-static-image" />
        <div className="hero-video-overlay" aria-hidden="true" />
      </div>
      <div className="apply-trial-content">
        <div className="home-hero-content">
          <div className="home-hero-section">
            <div className="container txt-contenido">
              {(isLoading || isAuthenticating) && (
                <div className="loading-overlay">
                  <div className="loading" />
                </div>
              )}
              <div className="login-container mb-5">
                <div className="form-apply w-full max-w-lg rounded-2xl">
                  <form onSubmit={handleSubmit}>
                    <h3 className="text-pink font-bold">LOG IN</h3>
                    <div className="text-left w-full" data-validate="Email is required">
                      <label className="text-xs font-semibold text-gray-600 ml-2 text-uppercase">Email</label>
                      <div>
                        <input
                          className="form-input"
                          id="email"
                          type="text"
                          name="email"
                          value={email}
                          onChange={handleChangeEmail}
                          required
                        />
                      </div>
                    </div>
                    <div className="text-left w-full mt-4" data-validate="Password is required">
                      <div className="flex justify-between">
                        <label className="text-xs font-semibold text-gray-600 ml-2 text-uppercase">
                          Password
                        </label>
                        <button type="button" className="eye-icon" onClick={togglePassVisibility}>
                          {isPassVisible ? (
                            <EyeIcon className="w-4 h-4 mb-1" />
                          ) : (
                            <EyeSlashIcon className="w-4 h-4 mb-1" />
                          )}
                        </button>
                      </div>
                      <div>
                        <input
                          onChange={handleChangePassword}
                          name="password"
                          id="password"
                          type={`${isPassVisible ? 'text' : 'password'}`}
                          className="form-input"
                          placeholder="Password"
                        />
                      </div>
                      <span className="focus-input100 d-flex align-items-center justify-content-end"></span>
                    </div>
                    <div className="text-right">
                      <a
                        href="true"
                        onClick={evt => {
                          handleReset(evt);
                        }}
                        className="text-gray-600 hover:text-blue-950 text-xs mr-2"
                      >
                        Forgot Password
                      </a>
                    </div>

                    <div className="container-contact100-form-btn mt-4">
                      <SharedButton type="submit" variant="primary" className="w-full">
                        Login
                      </SharedButton>
                    </div>
                    {errorMessage ? (
                      <div className="form-error bg-white p-4 w-3/4 mx-auto text-center rounded-xl border border-red-900 text-red-900">
                        <span className="flex flex-col items-center justify-center gap-2 text-sm">
                          <ExclamationCircleIcon className="w-5 h-5 mr-1" />
                          {errorMessage}
                        </span>
                        <br />
                        {errorMessage === 'User is disabled.' ? (
                          <div className="text-black text-xs font-light mt-2">
                            If you want to renew your account, please complete the{' '}
                            <a href={renewSurveyUrl} className="text-pink hover:underline font-semibold">
                              Renewal Survey
                            </a>{' '}
                            and confirm your account renewal{' '}
                          </div>
                        ) : null}
                        {errorMessage === UNCONFIRMED_USER_MESSAGE ? (
                          <ResendConfirmationEmailButton email={email} />
                        ) : null}
                      </div>
                    ) : null}
                  </form>
                  <div id="or">OR</div>
                  <SharedButton
                    variant="secondaryOutline"
                    className="w-40 mx-auto"
                    onClick={() => navigate('/sso')}
                  >
                    SSO Login
                  </SharedButton>
                  <div className="divider my-4"></div>
                  <div className="flex flex-col items-center justify-center mb-2">
                    <small className="text-gray-600">Don't have an account?</small>
                    <Link to="/plans" className="text-gray-900 font-semibold hover:text-blue-950">
                      See plans
                    </Link>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Login;
